import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, CalendarClock, FileBadge, GraduationCap, ShieldCheck, PlayCircle, Award, Eye, RefreshCcw } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [certs, setCerts] = useState([]);
  const [schedSlug, setSchedSlug] = useState(null);
  const [schedDate, setSchedDate] = useState("");
  const [preview, setPreview] = useState(null);

  const recertify = async (slug) => {
    try {
      const { data } = await api.post("/payments/recert-checkout", { course_slug: slug, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const load = () => {
    api.get("/my/enrollments").then((r) => setRows(r.data)).catch(() => {});
    api.get("/my/certificates").then((r) => setCerts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const download = async (slug, title) => {
    try {
      const res = await api.get(`/courses/${slug}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${title}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const schedule = async () => {
    if (!schedDate) return;
    try {
      await api.post("/exams/schedule", { course_slug: schedSlug, scheduled_at: new Date(schedDate).toISOString() });
      toast.success("Exam scheduled!");
      setSchedSlug(null); setSchedDate(""); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const canStart = (r) => r.scheduled_at && new Date(r.scheduled_at) <= new Date();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold mb-1">My account</p>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold text-white">Hi, {user?.name?.split(" ")[0] || "learner"}!</h1>
        </div>
        {user?.role === "admin" && (
          <Button onClick={() => nav("/admin")} className="btn-gold gap-2"><ShieldCheck className="h-4 w-4" />Admin panel</Button>
        )}
      </div>

      {certs.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-xl font-bold text-white mb-4 flex items-center gap-2"><Award className="h-5 w-5 text-gold" />My certificates</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {certs.map((c) => (
              <div key={c.number} className="bg-surface-card rounded-xl p-5 border border-gold/20" style={{ backgroundColor: "#131C38" }} data-testid={`cert-${c.number}`}>
                <FileBadge className="h-6 w-6 text-gold mb-2" />
                <p className="font-semibold text-white text-sm">{c.course_title}</p>
                <p className="text-xs font-mono text-gold mt-1">{c.number}</p>
                <p className="text-xs text-slate-400 mt-1">Score: {c.final_score} • Issued {c.issue_date}</p>
                {c.valid_until && <p className="text-xs text-slate-400">Valid until <span className="text-white">{c.valid_until}</span></p>}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10 bg-transparent" onClick={() => setPreview(c.number)} data-testid={`preview-cert-${c.number}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" />Preview
                  </Button>
                  <a href={`${API}/certificates/${encodeURIComponent(c.number)}/download`} target="_blank" rel="noreferrer" className="flex-1">
                    <Button size="sm" variant="outline" className="w-full border-gold/40 text-gold hover:bg-gold/10 bg-transparent">
                      <Download className="h-3.5 w-3.5 mr-2" />PDF
                    </Button>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10">
        <h2 className="font-display text-xl font-bold text-white mb-4 flex items-center gap-2"><GraduationCap className="h-5 w-5 text-gold" />My courses</h2>
        {rows.length === 0 ? (
          <div className="bg-surface rounded-xl p-10 border border-white/10 text-center">
            <p className="text-slate-300">You haven't purchased any courses yet.</p>
            <Button className="btn-gold mt-4" onClick={() => nav("/courses")} data-testid="empty-browse-btn">Explore courses</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((r) => (
              <div key={r.course_slug} className="bg-surface-card rounded-xl p-6 border border-white/10 grid lg:grid-cols-12 gap-4 items-center" style={{ backgroundColor: "#131C38" }} data-testid={`enrollment-${r.course_slug}`}>
                <div className="lg:col-span-5">
                  <p className="text-xs font-mono uppercase tracking-wider text-slate-400">{r.provider} • {r.category}</p>
                  <h3 className="font-display font-semibold text-white text-lg mt-1">{r.title}</h3>
                  <p className="text-xs text-slate-500 mt-1">{r.cert_line}</p>
                </div>
                <div className="lg:col-span-3 text-sm">
                  {r.certificate_number ? (
                    <span className="inline-flex items-center gap-1 text-emerald-400"><Award className="h-4 w-4" />Certificate earned</span>
                  ) : r.scheduled_at ? (
                    <span className="text-slate-300">Exam scheduled:<br /><span className="text-white">{new Date(r.scheduled_at).toLocaleString("en-GB")}</span></span>
                  ) : (
                    <span className="text-slate-400">Exam not scheduled</span>
                  )}
                </div>
                <div className="lg:col-span-4 flex flex-wrap gap-2 justify-end">
                  <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 bg-transparent" onClick={() => download(r.course_slug, r.title)} data-testid={`download-course-${r.course_slug}`}>
                    <Download className="h-4 w-4 mr-1" />Course PDF
                  </Button>
                  {(!r.certificate_number || r.recert_paid) && (
                    <Dialog open={schedSlug === r.course_slug} onOpenChange={(o) => { setSchedSlug(o ? r.course_slug : null); setSchedDate(""); }}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="secondary" data-testid={`schedule-btn-${r.course_slug}`}>
                          <CalendarClock className="h-4 w-4 mr-1" />{r.scheduled_at ? "Reschedule" : "Schedule"}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-surface border-white/10 text-white">
                        <DialogHeader><DialogTitle>Schedule the exam</DialogTitle></DialogHeader>
                        <p className="text-sm text-slate-400">Choose the date and time of your online exam for <span className="text-white">{r.title}</span>.</p>
                        <Input type="datetime-local" min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)} value={schedDate} onChange={(e) => setSchedDate(e.target.value)} data-testid="schedule-datetime-input"
                          className="bg-surface-2 border-white/10 text-white" />
                        <DialogFooter>
                          <Button className="btn-gold" onClick={schedule} data-testid="schedule-confirm-btn">Confirm</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                  {(!r.certificate_number || r.recert_paid) && canStart(r) && (
                    <Button size="sm" className="btn-gold" onClick={() => nav(`/exam/${r.course_slug}`)} data-testid={`start-exam-${r.course_slug}`}>
                      <PlayCircle className="h-4 w-4 mr-1" />Start exam
                    </Button>
                  )}
                  {r.certificate_number && !r.recert_paid && (
                    <Button size="sm" variant="secondary" onClick={() => recertify(r.course_slug)} data-testid={`recertify-${r.course_slug}`}>
                      <RefreshCcw className="h-4 w-4 mr-1" />Recertify
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {preview && (
        <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
          <DialogContent className="bg-surface border-white/10 text-white max-w-4xl">
            <DialogHeader><DialogTitle>Certificate preview — {preview}</DialogTitle></DialogHeader>
            <iframe title="Certificate preview" src={`${API}/certificates/${encodeURIComponent(preview)}/download?inline=1`}
              className="w-full h-[70vh] rounded-lg bg-white" data-testid="cert-preview-frame" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
